import { useEffect, useRef, useState, useCallback } from 'react';
import { BASE_WS_URL } from '../config/config';
import { markApiReachable } from './networkStatus';

export default function useProfileSocket(userId) {
    const [profileData, setProfileData] = useState(null);
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [readyState, setReadyState] = useState(WebSocket.CLOSED);

    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 10;

    const userIdRef = useRef(userId);
    // Track the userId a given connect() call belongs to, so we only clear
    // profileData when the user actually changes — not on every reconnect.
    const lastUserIdRef = useRef(userId);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    const clearReconnectTimeout = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    // Detach all handlers from a socket so it can no longer affect state,
    // then close it. Used whenever we're replacing or tearing down a
    // connection, so a socket that's on its way out can't fire a late
    // event (onmessage/onclose) that mutates state after it's been
    // superseded by a new connection (e.g. after a user switch).
    const detachAndClose = useCallback((socket, code, reason) => {
        if (!socket) return;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        try {
            socket.close(code, reason);
        } catch (err) {
            // socket may already be closing/closed - safe to ignore
        }
    }, []);

    const connect = useCallback(() => {
        if (!userIdRef.current) {
            return;
        }

        // Close existing socket if any, and strip its handlers first so it
        // can never call setState again once it's been replaced below.
        if (socketRef.current?.readyState === WebSocket.OPEN ||
            socketRef.current?.readyState === WebSocket.CONNECTING) {
            detachAndClose(socketRef.current, 1000, 'New connection requested');
        }

        // IMPORTANT: profileData is NOT reset here on every connect/reconnect.
        // Doing that caused balance to flash: null -> stale Redux fallback
        // -> fresh socket value, every single time the socket reconnected
        // (network blips, backoff retries, StrictMode double-invoke, etc).
        // We only clear it when the userId itself actually changes (see
        // the effect below), so a reconnect just silently keeps showing
        // the last known-good balance until a fresh PROFILE_UPDATE arrives.

        clearReconnectTimeout();

        const socket = new WebSocket(`${BASE_WS_URL}/user/profile`);

        socketRef.current = socket;

        setReadyState(WebSocket.CONNECTING);
        setError(null);

        // Every handler below checks `socketRef.current === socket` before
        // touching state. This guards against a stale/late event from a
        // socket that has since been replaced (e.g. this connection closing
        // after a newer one for a different user has already taken over)
        // from ever updating profileData / connection state.
        socket.onopen = () => {
            if (socketRef.current !== socket) return;

            setIsConnected(true);
            setReadyState(WebSocket.OPEN);
            reconnectAttemptsRef.current = 0;

            const payload = {
                type: 'subscribe',
                userId: userIdRef.current,
            };

            socket.send(JSON.stringify(payload));
        };

        socket.onmessage = (event) => {
            if (socketRef.current !== socket) return;

            try {
                const parsed = JSON.parse(event.data);

                if (parsed.type === 'PROFILE_UPDATE') {
                    markApiReachable();
                    // Merge: only overwrite fields the message actually sent,
                    // keep previous value for anything missing. Prevents a
                    // partial update from blanking balance/exp.
                    setProfileData((prev) => ({
                        balance:
                            parsed.data.balance !== undefined
                                ? parsed.data.balance
                                : prev?.balance,
                        exp:
                            parsed.data.exp !== undefined
                                ? parsed.data.exp
                                : prev?.exp,
                    }));
                } else if (parsed.type === 'FORCE_LOGOUT') {
                    detachAndClose(socket, 1000, 'Force logout');
                    if (socketRef.current === socket) {
                        socketRef.current = null;
                    }
                    clearReconnectTimeout();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            } catch (err) {
                // ignore malformed message
            }
        };

        socket.onerror = () => {
            if (socketRef.current !== socket) return;
            setError('WebSocket connection error occurred');
        };

        socket.onclose = (event) => {
            // This socket has already been superseded by a newer connection
            // (e.g. the user changed, or a manual reconnect happened) - its
            // close event is stale and must not affect current state, and
            // it must not schedule a reconnect either.
            if (socketRef.current !== socket) return;

            setIsConnected(false);
            setReadyState(WebSocket.CLOSED);
            socketRef.current = null;

            // Do not reconnect on normal closure (1000)
            if (event.code === 1000) {
                setError(null);
                return;
            }

            // Reconnect with exponential backoff (only if under max attempts)
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                const delay = Math.min(2000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
                reconnectAttemptsRef.current += 1;

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            } else {
                setError('Failed to connect to profile service after multiple attempts');
            }
        };
    }, [clearReconnectTimeout, detachAndClose]);

    useEffect(() => {
        if (userId) {
            // Only clear stale data when the user actually changed
            // (login as a different account). A reconnect for the SAME
            // user should never blank out the last known balance.
            if (lastUserIdRef.current !== userId) {
                setProfileData(null);
            }
            lastUserIdRef.current = userId;
            connect();
        } else {
            // Cleanup when no userId (logout)
            if (socketRef.current) {
                detachAndClose(socketRef.current, 1000, 'userId removed');
                socketRef.current = null;
            }
            setProfileData(null);
            lastUserIdRef.current = null;
        }

        const handleOffline = () => {
            clearReconnectTimeout();
            setIsConnected(false);
            setReadyState(WebSocket.CLOSED);
            if (socketRef.current) {
                detachAndClose(socketRef.current, 1000, 'network offline');
                socketRef.current = null;
            }
        };
        const handleOnline = () => {
            if (!userIdRef.current) return;
            reconnectAttemptsRef.current = 0;
            connect();
        };
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        // Cleanup on unmount / userId change
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            clearReconnectTimeout();
            if (socketRef.current) {
                detachAndClose(socketRef.current, 1000, 'component unmount');
                socketRef.current = null;
            }
        };
    }, [userId, connect, clearReconnectTimeout, detachAndClose]);

    const refresh = useCallback(() => {
        const socket = socketRef.current;
        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
                type: 'refresh',
                userId: userIdRef.current,
            }));
        }
    }, []);

    const reconnect = useCallback(() => {
        reconnectAttemptsRef.current = 0;
        connect();
    }, [connect]);

    return {
        profileData,
        error,
        isConnected,
        readyState,
        refresh,
        reconnect,
    };
}